import React from "react";
import Header from "../components/Header";

export default function ConstructionManagementPage() {
  return (
    <>
    <Header />
    <div className="w-full min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 text-[#003366] font-sans p-6">
      {/* Enhanced Header */}
      <div className="flex items-center gap-6 mb-8">
        <div className="relative">
          <div className="w-12 h-20 bg-gradient-to-b from-blue-500 to-blue-800 rounded-lg shadow-lg"></div>
          <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-orange-500 rounded-full"></div>
        </div>
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-900 to-blue-600 bg-clip-text text-transparent">
            5-Site Construction Management
          </h1>
          <p className="text-sm text-blue-700 mt-1">Digital Twin & Resource Management Platform</p>
        </div>
      </div>

      {/* Enhanced Points Section */}
      <div className="rounded-xl shadow-xl bg-gradient-to-r from-blue-50 to-white border border-blue-100 p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <svg className="w-6 h-6 text-blue-700" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <h2 className="text-xl font-bold text-blue-900">Core Process</h2>
        </div>
        <ul className="space-y-4">
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-blue-700 font-bold">1</span>
            </div>
            <p className="text-gray-700">
              <span className="font-semibold">Plan Decomposition:</span> Based on the P6 construction plan, work plans are systematically broken down into manageable tasks.
            </p>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-blue-700 font-bold">2</span>
            </div>
            <p className="text-gray-700">
              <span className="font-semibold">Resource Integration:</span> Using broken down work plans as a reference, drawings, materials, and human resources are dynamically linked.
            </p>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-blue-700 font-bold">3</span>
            </div>
            <p className="text-gray-700">
              <span className="font-semibold">Digital Twin:</span> Through multi-disciplinary tracking and statistical analysis combined with three-dimensional modeling, achieve real-time digital twin display of construction progress and resource matching.
            </p>
          </li>
        </ul>
      </div>

      {/* Enhanced Navigation Tabs */}
      <div className="rounded-xl shadow-lg bg-gradient-to-r from-blue-900 to-blue-700 p-1 mb-8">
        <div className="flex overflow-x-auto scrollbar-hide">
          {[
            { label: "Plan Import", icon: "📥" },
            { label: "Plan Break-down", icon: "📋" },
            { label: "Construction Tasks", icon: "👷", active: true },
            { label: "Materials Simulation", icon: "📊" },
            { label: "Work Package", icon: "📦" },
            { label: "Progress Tracking", icon: "📈" },
            { label: "Consolidation & Display", icon: "🖥️" }
          ].map((item, index) => (
            <div
              key={index}
              className={`flex items-center gap-2 px-4 py-3 whitespace-nowrap transition-all duration-300 ${item.active
                  ? "bg-white text-blue-900 rounded-lg shadow-md font-bold"
                  : "text-white hover:bg-blue-700/50"
                }`}
            >
              <span>{item.icon}</span>
              <span className="text-sm font-medium">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Enhanced Two‑Column Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Card - Construction Workflow */}
        <div className="rounded-2xl shadow-2xl overflow-hidden bg-white transform transition-transform hover:scale-[1.02]">
          <div className="bg-gradient-to-r from-orange-600 to-orange-500 text-white py-4 px-6 font-bold">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
              </svg>
              Construction Workflow Diagram
            </div>
          </div>
          <div className="p-4">
            <img
              src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
              alt="Construction Planning Workflow"
              className="w-full h-72 object-cover rounded-lg"
            />
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-2">Workflow Integration</h3>
              <p className="text-sm text-gray-600">
                Seamless integration of P6 plans with resource allocation, material tracking, and progress monitoring
                for comprehensive construction management.
              </p>
            </div>
          </div>
        </div>

        {/* Right Card - Digital Twin */}
        <div className="rounded-2xl shadow-2xl overflow-hidden bg-white transform transition-transform hover:scale-[1.02]">
          <div className="bg-gradient-to-r from-purple-600 to-purple-500 text-white py-4 px-6 font-bold">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
              Digital Twin Visualization
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-center justify-center mb-2">
              <span className="px-3 py-1 bg-red-100 text-red-600 rounded-full text-sm font-semibold">
                Model Correlation Status: Active
              </span>
            </div>
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
              alt="Digital Twin 3D Model Interface"
              className="w-full h-72 object-cover rounded-lg"
            />
            <div className="mt-4 p-4 bg-purple-50 rounded-lg">
              <h3 className="font-semibold text-purple-900 mb-2">Real-time Progress Monitoring</h3>
              <p className="text-sm text-gray-600">
                Interactive 3D model showing live construction progress with color-coded status indicators
                for different phases and resources.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats & Metrics Section */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-xl shadow-md border border-blue-100 hover:shadow-lg transition-shadow">
          <div className="text-2xl font-bold text-blue-800 flex items-center gap-2">
            95%
            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="text-sm text-gray-600">Schedule Accuracy</div>
        </div>
        <div className="bg-white p-5 rounded-xl shadow-md border border-blue-100 hover:shadow-lg transition-shadow">
          <div className="text-2xl font-bold text-blue-800">30%</div>
          <div className="text-sm text-gray-600">Resource Utilization Increase</div>
        </div>
        <div className="bg-white p-5 rounded-xl shadow-md border border-blue-100 hover:shadow-lg transition-shadow">
          <div className="text-2xl font-bold text-blue-800">500+</div>
          <div className="text-sm text-gray-600">Active Projects</div>
        </div>
        <div className="bg-white p-5 rounded-xl shadow-md border border-blue-100 hover:shadow-lg transition-shadow">
          <div className="text-2xl font-bold text-blue-800">99.9%</div>
          <div className="text-sm text-gray-600">Model-Plan Sync Rate</div>
        </div>
      </div>

      {/* Feature Highlights */}
      <div className="mt-8 bg-gradient-to-r from-blue-900 to-blue-800 rounded-2xl p-6 text-white">
        <h3 className="text-xl font-bold mb-4">Key Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold">Real-time 3D Visualization</h4>
              <p className="text-sm text-blue-200 mt-1">Live digital twin of construction site</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold">Predictive Analytics</h4>
              <p className="text-sm text-blue-200 mt-1">AI-powered schedule forecasting</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold">Risk Mitigation</h4>
              <p className="text-sm text-blue-200 mt-1">Early detection of potential delays</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}